import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { ipApi } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';

/* ═══════════════════════════════════════════════════════════
   TELEMEDICINE / VIDEO CONSULT MODULE
   - Video Consultation Lobby
   - e-Prescription after call
   - Follow-up Scheduling
   - Patient Queue & Status
   - Consultation History
   - Telemedicine Guidelines (MCI/NMC)
═══════════════════════════════════════════════════════════ */

function VideoCallRoom({ patient, onEnd, onSaveNotes, doctorName }) {
  const [elapsed, setElapsed] = useState(0);
  const [notes, setNotes] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [endPrompt, setEndPrompt] = useState(false);
  const [consultFee, setConsultFee] = useState('');

  // Use stable hospital-scoped room name stored on record; fall back to generated
  const generatedRoom = React.useMemo(() => {
    const raw = String(patient?.patientId || patient?.id || patient?.recordId || 'consult')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 20);
    return `HCEN${raw}`.slice(0, 40) || `HCENconsult${Date.now().toString(36)}`;
  }, [patient]);
  const roomName = patient?.meetingRoom || generatedRoom;

  const displayName = doctorName || 'Doctor';
  const jitsiSrc = `https://meet.jit.si/${roomName}#userInfo.displayName="${encodeURIComponent(displayName)}"&config.prejoinPageEnabled=false`;
  const shareLink = `https://meet.jit.si/${roomName}`;

  React.useEffect(() => {
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const waShare = patient?.phone
    ? `https://wa.me/${String(patient.phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Join your HCEN video consult: ${shareLink}`)}`
    : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14, minHeight: 500 }}>
      <div className="info-banner full" style={{ gridColumn: '1 / -1' }}>
        Live video via Jitsi Meet · Share the link with the patient · Duration {formatTime(elapsed)}
        {waShare && (
          <>
            {' · '}
            <a href={waShare} target="_blank" rel="noreferrer">Send join link on WhatsApp</a>
          </>
        )}
        {' · '}
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          style={{ marginLeft: 6 }}
          onClick={() => { navigator.clipboard?.writeText(shareLink); }}
        >
          Copy meeting link
        </button>
      </div>
      <div style={{ borderRadius: 16, overflow: 'hidden', minHeight: 420, background: '#0f172a', border: '1px solid #1e293b' }}>
        <iframe
          title="HCEN video consult"
          src={jitsiSrc}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          style={{ width: '100%', height: '100%', minHeight: 420, border: 0 }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ borderLeft: '4px solid #0d9488' }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{patient.patient}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{patient.age}y / {patient.gender === 'M' ? 'Male' : 'Female'} · {patient.city || '—'}</div>
          <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: '#0d6e6a', marginBottom: 2 }}>Chief Complaint:</div>
            <div>{patient.complaint || '—'}</div>
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13, color: '#475569' }}>Consultation Notes</div>
          <textarea
            className="input"
            rows={8}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={"S: Chief complaint...\nO: Findings...\nA: Assessment...\nP: Plan..."}
            style={{ fontSize: 13, lineHeight: 1.6 }}
          />
        </div>
        {endPrompt ? (
          <div className="card" style={{ borderLeft: '4px solid #f59e0b', padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#92400e' }}>End consult — optional fee</div>
            <div className="field mb">
              <label style={{ fontSize: 12 }}>Consult fee (₹) — leave blank to skip</label>
              <input
                type="number"
                className="input"
                placeholder="0"
                value={consultFee}
                onChange={(e) => setConsultFee(e.target.value)}
                style={{ width: 120 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEndPrompt(false); setConsultFee(''); }}>Back</button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onEnd({ fee: Number(consultFee) || 0, durationSec: elapsed })}
              >Confirm end</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={saveBusy || !notes.trim()}
              onClick={async () => {
                if (!onSaveNotes) return;
                setSaveBusy(true);
                try {
                  await onSaveNotes({
                    patientName: patient.patient,
                    patientId: patient.patientId,
                    notes,
                    durationSec: elapsed,
                    meetingUrl: shareLink,
                  });
                  setNotesSaved(true);
                  setTimeout(() => setNotesSaved(false), 2500);
                } catch (e) {
                  window.alert(e.message || 'Could not save notes');
                } finally {
                  setSaveBusy(false);
                }
              }}
            >
              {saveBusy ? 'Saving…' : notesSaved ? 'Saved' : 'Save Notes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEndPrompt(true)}>End session</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EPrescribeModal({ patient, onClose, onSave, doctorName, doctorReg, hospitalName }) {
  const [rxLines, setRxLines] = useState([{ drug: '', dose: '', freq: 'OD', days: 5, instructions: 'After meals' }]);
  const [advice, setAdvice] = useState('');
  const [followUp, setFollowUp] = useState('2 weeks');
  const [saved, setSaved] = useState(false);
  const FREQ = ['OD', 'BD', 'TDS', 'QID', 'HS', 'SOS', 'Weekly', 'Once'];
  const docLine = `${doctorName || 'Consulting Doctor'}${doctorReg ? ` · Reg. No: ${doctorReg}` : ''}${hospitalName ? ` · ${hospitalName}` : ''}`;

  function print({ draft = false } = {}) {
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write('<html><head><title>e-Prescription</title><style>body{font-family:Arial,sans-serif;max-width:700px;margin:30px auto}table{width:100%;border-collapse:collapse;margin:16px 0}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left}th{background:#f1f5f9;font-weight:700}.header{text-align:center;border-bottom:2px solid #0d6e6a;padding-bottom:12px;margin-bottom:20px}.draft{color:#b45309;font-weight:800;border:2px dashed #f59e0b;padding:8px;text-align:center;margin-bottom:12px}</style></head><body>');
    if (draft) win.document.write('<div class="draft">UNSAVED DRAFT — not a valid prescription until Save &amp; Send</div>');
    win.document.write('<div class="header"><h2>e-Prescription (Telemedicine)</h2><p>' + esc(docLine) + ' · Date: ' + new Date().toLocaleDateString('en-IN') + '</p></div>');
    win.document.write('<p><strong>Patient:</strong> ' + esc(patient?.patient || '') + ' | <strong>Age:</strong> ' + esc(patient?.age || '') + ' | <strong>Type:</strong> Telemedicine Consultation</p>');
    win.document.write('<table><thead><tr><th>Drug</th><th>Dose</th><th>Frequency</th><th>Days</th><th>Instructions</th></tr></thead><tbody>');
    rxLines.forEach(r => { win.document.write('<tr><td>' + esc(r.drug) + '</td><td>' + esc(r.dose) + '</td><td>' + esc(r.freq) + '</td><td>' + esc(r.days) + '</td><td>' + esc(r.instructions) + '</td></tr>'); });
    win.document.write('</tbody></table>');
    if (advice) win.document.write('<p><strong>Advice:</strong> ' + esc(advice) + '</p>');
    win.document.write('<p><strong>Follow-up:</strong> ' + esc(followUp) + '</p>');
    win.document.write('<p style="margin-top:40px;color:#64748b;font-size:12px">This is a valid e-prescription issued via telemedicine as per MoHFW Telemedicine Guidelines 2020 (NMC Act).</p>');
    win.document.write('</body></html>'); win.document.close(); win.print();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#0d6e6a' }}>💊 e-Prescription — {patient?.patient}</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
        <button type="button" className="btn btn-secondary btn-sm mb" onClick={() => setRxLines(r => [...r, { drug: '', dose: '', freq: 'OD', days: 5, instructions: 'After meals' }])}>+ Add Medicine</button>
        {rxLines.map((line, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 60px 2fr 30px', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <input className="input" placeholder="Drug name" value={line.drug} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, drug: e.target.value } : l))} />
            <input className="input" placeholder="Dose" value={line.dose} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, dose: e.target.value } : l))} />
            <select className="input" value={line.freq} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, freq: e.target.value } : l))}>{FREQ.map(f => <option key={f}>{f}</option>)}</select>
            <input type="number" className="input" placeholder="Days" value={line.days} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, days: e.target.value } : l))} />
            <input className="input" placeholder="Instructions" value={line.instructions} onChange={e => setRxLines(r => r.map((l, j) => j === i ? { ...l, instructions: e.target.value } : l))} />
            {rxLines.length > 1 && <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }} onClick={() => setRxLines(r => r.filter((_, j) => j !== i))}>×</button>}
          </div>
        ))}
        <div className="field mb"><label>Advice / Instructions</label><textarea className="input" rows={2} value={advice} onChange={e => setAdvice(e.target.value)} placeholder="Dietary advice, lifestyle modifications..." /></div>
        <div className="field mb"><label>Follow-up</label><select className="input" value={followUp} onChange={e => setFollowUp(e.target.value)}><option>1 week</option><option>2 weeks</option><option>1 month</option><option>3 months</option><option>6 months</option><option>As needed (SOS)</option></select></div>
        <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#92400e' }}>📌 Compliant with MoHFW Telemedicine Practice Guidelines 2020 (NMC Act). Controlled substances cannot be prescribed via telemedicine.</div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-secondary" onClick={() => print({ draft: !saved })} title={saved ? 'Print saved Rx' : 'Print draft (watermarked)'}>
            🖨 {saved ? 'Print Rx' : 'Print draft'}
          </button>
          <button type="button" className="btn btn-primary" onClick={async () => {
            if (onSave) {
              try {
                await onSave({
                  patient: patient?.patient,
                  patientId: patient?.patientId,
                  rxLines,
                  advice,
                  followUp,
                });
                setSaved(true);
              } catch (e) {
                window.alert(e.message || 'Could not save prescription');
                return;
              }
            } else {
              setSaved(true);
            }
            print({ draft: false });
            onClose();
          }}>✓ Send & Save Rx</button>
        </div>
      </div>
    </div>
  );
}

export default function TelemedicinePage() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { records: queueRecords, create, patch } = useHmsRecords('telemedicine-queue');
  const { create: createConsult } = useHmsRecords('telemedicine-consult');
  const [tab, setTab] = useState('queue');
  const [activeCall, setActiveCall] = useState(null);
  const [showRx, setShowRx] = useState(null);
  const [bookForm, setBookForm] = useState({ patient: '', patientId: '', age: '', gender: 'F', phone: '', complaint: '', doctor: '', scheduled: '', type: 'Follow-up', city: '' });
  const [showBook, setShowBook] = useState(false);
  const [msg, setMsg] = useState('');
  const [bookBusy, setBookBusy] = useState(false);

  const queue = queueRecords;

  const saveConsultNotes = async (body) => {
    await createConsult({
      ...body,
      status: 'Completed',
      at: new Date().toISOString(),
    });
  };

  const saveRx = async ({ patient: patientName, patientId, rxLines, advice, followUp }) => {
    const linkedId = String(patientId || showRx?.patientId || '').trim();
    if (!linkedId) {
      setMsg('Link or create a patient record before saving e-prescription (UHID required).');
      throw new Error('Patient UHID required for telemedicine Rx');
    }
    if (!patientName) {
      setMsg('Patient name is required for e-prescription.');
      throw new Error('Patient name required');
    }
    const recordId = `EMR-TELE-${Date.now()}`;
    await ipApi.saveEmr({
      recordId,
      patientId: linkedId,
      patientName,
      visitType: 'Telemedicine',
      status: 'completed',
      diagnosis: advice || 'Telemedicine consult',
      assessment: { primary: advice || 'Telemedicine consult' },
      rxLines: (rxLines || []).filter((l) => l.drug),
      prescription: (rxLines || []).filter((l) => l.drug).map((l) => `${l.drug} ${l.dose} ${l.freq} x ${l.days}d — ${l.instructions}`).join('; '),
      plan: { advice: advice || '', followUp: followUp || '' },
      followUp,
      createdAt: new Date().toISOString(),
    });
    setMsg('e-Prescription saved to patient EMR.');
  };

  const updateStatus = async (id, status) => {
    const row = queue.find((q) => q.id === id || q.recordId === id);
    if (!row) return;
    try {
      await patch(row.recordId || row.id, { status });
    } catch (e) {
      setMsg(e.message || 'Could not update consult status');
    }
  };

  const startCall = async (p) => {
    const hId = String(user?.hospitalId || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
    const pId = String(p.patientId || p.id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 14);
    const roomName = (`HCEN${hId}${pId}`).slice(0, 40) || `HCENconsult${Date.now().toString(36)}`;
    const row = queue.find((q) => q.id === p.id || q.recordId === p.recordId);
    try {
      if (row) await patch(row.recordId || row.id, { status: 'In Call', meetingRoom: roomName });
    } catch (_) {}
    setActiveCall({ ...p, meetingRoom: roomName });
    setTab('call');
  };

  const bookConsult = async (e) => {
    e.preventDefault();
    if (!bookForm.patient.trim()) { setMsg('Patient name is required.'); return; }
    if (!bookForm.doctor.trim()) { setMsg('Doctor name is required.'); return; }
    if (!bookForm.phone.trim()) { setMsg('Patient phone is required.'); return; }
    setBookBusy(true);
    setMsg('');
    try {
      let patientId = String(bookForm.patientId || '').trim();
      if (!patientId) {
        const found = await ipApi.getPatients(bookForm.phone.trim(), 20);
        const match = (found?.patients || []).find((p) =>
          String(p.phone || '').replace(/\D/g, '').endsWith(String(bookForm.phone).replace(/\D/g, '').slice(-10))
        );
        if (match?.patientId) {
          patientId = match.patientId;
        } else {
          const created = await ipApi.createPatient({
            name: bookForm.patient.trim(),
            phone: bookForm.phone.trim(),
            age: Number(bookForm.age) || 0,
            gender: bookForm.gender,
            address: bookForm.city || '',
          });
          patientId = created?.patient?.patientId || created?.patientId || '';
        }
      }
      if (!patientId) throw new Error('Could not link patient UHID — create patient in Reception first.');
      const id = `TELE-${Date.now()}`;
      await create({
        id,
        ...bookForm,
        patientId,
        age: Number(bookForm.age) || 0,
        status: 'Waiting',
      });
      setShowBook(false);
      setBookForm({ patient: '', patientId: '', age: '', gender: 'F', phone: '', complaint: '', doctor: '', scheduled: '', type: 'Follow-up', city: '' });
      setMsg(`Video consult booked · UHID ${patientId}`);
    } catch (err) {
      setMsg(err.message || 'Could not book consult');
    } finally {
      setBookBusy(false);
    }
  };

  const statusColor = { Waiting: '#f59e0b', 'In Call': '#10b981', Completed: '#6366f1', Cancelled: '#94a3b8' };

  return (
    <PageShell
      title="Telemedicine / Video Consult"
      subtitle="Virtual OPD · live Jitsi video · e-Prescription linked to patient UHID · MoHFW guidelines"
      actions={<button type="button" className="btn btn-primary" onClick={() => setShowBook((s) => !s)}>+ Book Video Consult</button>}
    >
      {msg && <div className={`mb ${/fail|error|could|required/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}
      {showBook && (
        <form className="card mb" onSubmit={bookConsult} style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 style={{ marginTop: 0 }}>Book Video Consult</h3>
          <div className="form-grid mb">
            <div className="field"><label>Patient Name *</label><input className="input" required value={bookForm.patient} onChange={(e) => setBookForm((f) => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>UHID (optional)</label><input className="input" value={bookForm.patientId} onChange={(e) => setBookForm((f) => ({ ...f, patientId: e.target.value }))} placeholder="Auto-link by phone if blank" /></div>
            <div className="field"><label>Age</label><input type="number" className="input" value={bookForm.age} onChange={(e) => setBookForm((f) => ({ ...f, age: e.target.value }))} /></div>
            <div className="field"><label>Gender</label><select className="input" value={bookForm.gender} onChange={(e) => setBookForm((f) => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option></select></div>
            <div className="field"><label>Phone *</label><input className="input" required value={bookForm.phone} onChange={(e) => setBookForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            <div className="field"><label>Doctor *</label><input className="input" required value={bookForm.doctor} onChange={(e) => setBookForm((f) => ({ ...f, doctor: e.target.value }))} placeholder="Dr. Sharma" /></div>
            <div className="field"><label>Scheduled Time</label><input className="input" value={bookForm.scheduled} onChange={(e) => setBookForm((f) => ({ ...f, scheduled: e.target.value }))} placeholder="10:30" /></div>
            <div className="field full"><label>Complaint</label><input className="input" value={bookForm.complaint} onChange={(e) => setBookForm((f) => ({ ...f, complaint: e.target.value }))} /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowBook(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={bookBusy}>{bookBusy ? 'Saving…' : 'Save Booking'}</button>
          </div>
        </form>
      )}

      <div className="info-banner mb">
        Booking creates/links a real patient UHID. Start Call opens a live Jitsi meeting — share the link via WhatsApp. e-Prescription saves only when UHID is linked.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Scheduled Today', val: queue.length, color: '#6366f1' }, { label: 'Waiting', val: queue.filter(q => q.status === 'Waiting').length, color: '#f59e0b' }, { label: 'In Call', val: queue.filter(q => q.status === 'In Call').length, color: '#10b981' }, { label: 'Completed', val: queue.filter(q => q.status === 'Completed').length, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="period-tabs card mb">
        {[{ id: 'queue', label: '📋 Patient Queue' }, { id: 'call', label: '📹 Video Room', disabled: !activeCall }, { id: 'history', label: '📜 History' }].map(({ id, label, disabled }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => !disabled && setTab(id)} style={{ opacity: disabled ? 0.4 : 1 }}>{label}</button>
        ))}
      </div>

      {tab === 'queue' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {queue.filter((p) => !['Completed', 'Cancelled', 'No-show'].includes(p.status)).map(p => (
            <div key={p.id || p.recordId} className="card" style={{ borderLeft: `4px solid ${statusColor[p.status] || '#94a3b8'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{p.patient} <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{p.age}y / {p.gender === 'M' ? 'Male' : 'Female'} · {p.city}</span></div>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>📋 {p.complaint}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{p.doctor} · {p.scheduled} · {p.type}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: (statusColor[p.status] || '#94a3b8') + '18', color: statusColor[p.status] || '#94a3b8', border: `1px solid ${(statusColor[p.status] || '#94a3b8')}40` }}>{p.status}</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.status !== 'Completed' && <button type="button" className="btn btn-primary btn-sm" onClick={() => startCall(p)}>📹 Start Call</button>}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowRx(p)}>💊 e-Prescribe</button>
                    {p.status === 'Waiting' && (
                      <button type="button" className="btn btn-secondary btn-sm" style={{ color: '#dc2626' }} onClick={() => updateStatus(p.id || p.recordId, 'No-show')}>No-show</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {queue.filter((p) => !['Completed', 'Cancelled', 'No-show'].includes(p.status)).length === 0 && (
            <p className="hint table-empty">No active consults. Book a video consult to begin.</p>
          )}
        </div>
      )}

      {tab === 'call' && activeCall && (
        <VideoCallRoom
          patient={activeCall}
          doctorName={user?.name}
          onSaveNotes={saveConsultNotes}
          onEnd={async ({ fee = 0 } = {}) => {
            if (fee > 0 && activeCall?.patientId) {
              try {
                await ipApi.createOpReceipt({
                  patientId: activeCall.patientId,
                  patientName: activeCall.patient,
                  service: 'Telemedicine Consult',
                  amount: fee,
                  mode: 'Cash',
                });
              } catch (_) {}
            }
            await updateStatus(activeCall.id || activeCall.recordId, 'Completed');
            setActiveCall(null);
            setTab('queue');
          }}
        />
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card" style={{ padding: '10px 14px', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#64748b' }}>Completed, cancelled and no-show consults — {queue.filter((p) => ['Completed', 'Cancelled', 'No-show'].includes(p.status)).length} records</span>
          </div>
          {queue.filter((p) => ['Completed', 'Cancelled', 'No-show'].includes(p.status)).map(p => (
            <div key={p.id || p.recordId} className="card" style={{ borderLeft: `4px solid ${statusColor[p.status] || '#94a3b8'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{p.patient} <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{p.age}y / {p.gender === 'M' ? 'Male' : 'Female'}</span></div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{p.doctor} · {p.scheduled} · {p.complaint}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: (statusColor[p.status] || '#94a3b8') + '18', color: statusColor[p.status] || '#94a3b8', border: `1px solid ${(statusColor[p.status] || '#94a3b8')}40` }}>{p.status}</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowRx(p)}>💊 e-Prescribe</button>
                </div>
              </div>
            </div>
          ))}
          {queue.filter((p) => ['Completed', 'Cancelled', 'No-show'].includes(p.status)).length === 0 && (
            <p className="hint table-empty">No completed or cancelled consults yet.</p>
          )}
        </div>
      )}

      {showRx && (
        <EPrescribeModal
          patient={showRx}
          onClose={() => setShowRx(null)}
          onSave={saveRx}
          doctorName={user?.name || showRx?.doctor}
          doctorReg={user?.regNo || user?.registrationNo || ''}
          hospitalName={hospital?.name || user?.hospital || ''}
        />
      )}
    </PageShell>
  );
}
